import { Link } from 'react-router-dom';
import type { ArticleDetail } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { formatDateTime } from '@/lib/format';
import { ArticleImage } from '@/components/content';
import { Badge } from '@/components/ui';

/**
 * The article's masthead — PRD §14: category, headline, subheadline, byline with photo,
 * timestamps in `Asia/Kolkata` (§1.12), featured image with caption and credit.
 *
 * The headline is the page's `<h1>`, which is why the body renderer clamps its own
 * headings to h2–h4 (Phase 5 does that at write time, not here).
 */
const YOUTUBE_ID_REGEX =
  /(?:(?:m\.|www\.)?youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=|v\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/;

export function getSafeEmbedUrl(url: string | null | undefined): string {
  if (!url || typeof url !== 'string') return '';
  const match = url.trim().match(YOUTUBE_ID_REGEX);
  if (match && match[1]) {
    return `https://www.youtube-nocookie.com/embed/${match[1]}`;
  }
  return url.trim();
}

export function ArticleHeader({ article }: { article: ArticleDetail }) {
  const { locale, t } = useLocale();

  return (
    <header className="mx-auto max-w-3xl px-gutter pt-8">
      {/*
        The breaking banner (Phase 11, PRD §5). A story the desk marked `is_breaking`
        says so on the page itself, not only in the ticker a reader may have scrolled
        past — and it is `role="status"`, so a screen reader hears it with the headline
        rather than as an unannounced red box.
      */}
      {article.isBreaking && (
        <p
          role="status"
          data-breaking-banner
          className="mb-4 flex items-center gap-2 rounded-md border border-red/30 bg-red/5 px-3.5 py-2.5 text-sm font-extrabold uppercase tracking-wide text-red"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-red" aria-hidden="true" />
          {t('article.breaking')}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={article.category.path}>
          <Badge tone="category" color={article.category.color}>
            {article.category.name}
          </Badge>
        </Link>

        {article.translation && (
          <Link
            to={article.translation.path}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-soft/80 px-3 py-1 text-xs font-semibold text-body transition hover:border-red hover:text-red"
          >
            <span className="text-muted">
              {article.translation.locale === 'en' ? 'Read in English:' : 'অসমীয়াত পঢ়ক:'}
            </span>
            <span className="truncate max-w-[200px] sm:max-w-xs">
              {article.translation.headline}
            </span>
            <span aria-hidden="true">→</span>
          </Link>
        )}
      </div>

      <h1 className="mt-3 text-4xl font-extrabold text-ink">{article.headline}</h1>

      {article.subheadline && (
        <p className="mt-3 text-xl font-normal leading-snug text-body">{article.subheadline}</p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3 border-y border-line py-3.5">
        <Link to={article.author.path} className="group flex items-center gap-2.5">
          {article.author.photo ? (
            <ArticleImage
              image={article.author.photo}
              alt={article.author.name}
              sizes="36px"
              aspect="aspect-square"
              className="h-9 w-9 shrink-0 overflow-hidden rounded-full"
            />
          ) : null}
          <span className="text-base font-bold text-ink transition group-hover:text-red">
            {article.author.name}
          </span>
        </Link>

        <span className="text-xs text-muted">
          {formatDateTime(article.publishedAt, locale)} ·{' '}
          {t('meta.readTime', { minutes: article.readingMinutes })}
        </span>
      </div>

      {article.videoUrl ? (
        <div className="mt-6 flex flex-col gap-4" data-article-video-hero>
          <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black shadow-sm">
            <iframe
              src={getSafeEmbedUrl(article.videoUrl)}
              title={article.headline}
              className="absolute inset-0 h-full w-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          {article.image && (
            <figure className="mt-2">
              <ArticleImage
                image={article.image}
                alt={article.headline}
                priority={false}
                sizes="(max-width: 1080px) 100vw, 760px"
                className="overflow-hidden rounded-md max-h-96 object-cover"
              />
              {(article.image.caption || article.image.credit) && (
                <figcaption className="mt-2 text-xs text-muted">
                  {article.image.caption}
                  {article.image.caption && article.image.credit ? ' · ' : ''}
                  {article.image.credit}
                </figcaption>
              )}
            </figure>
          )}
        </div>
      ) : article.image ? (
        <figure className="mt-6">
          <ArticleImage
            image={article.image}
            alt={article.headline}
            priority
            sizes="(max-width: 1080px) 100vw, 760px"
            className="overflow-hidden rounded-md"
          />
          {(article.image.caption || article.image.credit) && (
            <figcaption className="mt-2 text-xs text-muted">
              {article.image.caption}
              {article.image.caption && article.image.credit ? ' · ' : ''}
              {article.image.credit}
            </figcaption>
          )}
        </figure>
      ) : null}
    </header>
  );
}
